import { all, call, put, race, select, take } from 'redux-saga/effects';
import { push, replace, unblock } from 'redux-little-router';
import { get, isNil } from 'lodash';
import { getFormValues } from 'redux-form/immutable';
import {
  create3NDFLXmlDraft,
  get3NDFLDrafts,
  load3NDFLDraft,
  new3NDFLShortDraft,
  save3NDFLDraft,
} from 'modules/Taps/services/API';
import { copy } from 'modules/Taps/services/API/3ndflView';
import * as actions from '../redux/actions';
import { selectDraftMode } from 'modules/Taps/components/NDFL3Light/selectors';
import * as actionTypes from 'modules/Taps/components/NDFL3Light/redux/actionTypes';
import { STEP_INITIAL } from 'modules/Taps/components/NDFL3Light/constants';
import { startRequest } from 'modules/Authorization/redux/logout/actions';
import config from 'config';
import { remotePaths } from 'router/routes';

export function* createDraftSaga({ payload: { reportYear, reportMode } }) {
  const { isOk, data, error } = yield call(new3NDFLShortDraft, reportYear, reportMode);

  if (isOk) {
    yield put(actions.createDraftSuccess(data));
  } else {
    const errorStatus = get(error, 'response.status');
    yield put(
      actions.createDraftFailure(
        errorStatus !== 500 ? get(error, 'response.data.reason') : { description: 'serverError' },
      ),
    );
  }
}

export function* refineDraftSaga({ payload: { declarationId } }) {
  const mode = yield select(selectDraftMode);
  const { isOk, data, error } = yield call(copy, declarationId, mode);

  if (isOk) {
    yield put(actions.refineDraftSuccess(data));
  } else {
    const errorStatus = get(error, 'response.status');
    yield put(
      actions.refineDraftFailure(
        errorStatus !== 500 ? get(error, 'response.data.reason') : { description: 'serverError' },
      ),
    );
  }
}

export function* getDraftSaga({ payload: { cardId } }) {
  const { isOk, data, error } = yield call(load3NDFLDraft, cardId);

  if (isOk) {
    yield put(actions.getDraftSuccess(data));
  } else {
    yield put(actions.getDraftFailure(error));
  }
}

export function* updateDraftSaga({ payload: { draft } }) {
  const { isOk, data, error } = yield call(
    save3NDFLDraft,
    draft,
    draft.getIn(['payload', 'initialData', 'reportYear']),
  );

  if (isOk) {
    yield put(actions.updateDraftSuccess(data));
  } else {
    yield put(actions.updateDraftFailure(error));
  }
}

export function* updateXmlDraftSaga({ payload: draft } = {}) {
  const { isOk, data, error } = yield call(
    create3NDFLXmlDraft,
    draft.toJS(),
    draft.getIn(['payload', 'initialData', 'reportYear']),
  );

  if (isOk) {
    yield put(actions.updateXmlDraftSuccess(data));
  } else {
    yield put(actions.updateXmlDraftFailure(error));
  }
}

export function* checkSavedDraftSaga({ payload: currentMode }) {
  const { isOk, data, error } = yield call(get3NDFLDrafts, { scope: 'mode', mode: currentMode });

  if (isOk) {
    yield put(
      actions.getSavedDraftsSuccess(
        data.find(({ statusCode, external }) => statusCode === 0 && !external),
      ),
    );
  } else {
    yield put(actions.getSavedDraftsFailure(error));
  }
}

export function* getDraftByCardId({ cardId, step }) {
  yield put(actions.getDraft(cardId));

  const draft = yield race({
    success: take(actionTypes.GET_DRAFT_SUCCESS),
    failure: take(actionTypes.GET_DRAFT_FAILURE),
  });

  if (draft.success) {
    yield put(replace({ query: { cardId, step: isNil(step) ? STEP_INITIAL : step } }));
  }

  if (draft.failure) {
    yield config.goToNewLK(remotePaths.situations);
  }
}

export function* updateCurrentDraftSaga({ payload: cardId }) {
  yield getDraftByCardId({ cardId });
  yield put(actions.resetSavedDraft());
}

export function* saveDraftAndExitSaga({ payload: { formName, location } }) {
  const draft = yield select(getFormValues(formName));
  const logout = yield select(({ taps: { ndfl3: { logout } = {} } = {} }) => logout);
  yield put(actions.updateDraft({ draft }));
  yield take(actionTypes.UPDATE_DRAFT_SUCCESS);
  yield all([put(unblock()), logout ? put(startRequest()) : put(push(location))]);
}
